import React, { FC } from 'react';
import {
  ControlledInput,
  ControlledSelect,
} from '@uikit/FormItems/ControlledItems';
import Modal from '@uikit/Modal/Modal';
import TableSection from '@uikit/Tables/TableSection/TableSection';
import { useContractorSearch } from '@hooks/useContractorSearch';
import { validations } from '@utils/validations';
import './style.scss';

type ContractorSearchModalProps = {
  closeModal: () => void;
};

const ContractorSearchModal: FC<ContractorSearchModalProps> = ({
  closeModal,
}) => {
  const {
    control,
    states,
    contractorsType,
    contractorsAtms,
    contractorsLos,
    loadLos,
    loadContractorTypes,
    loadAtms,
    loadStates,
    searchContractorData,
    isContractorLoading,
    isAuxConEnabled,
    filters,
    handleClickEdit,
  } = useContractorSearch();

  return (
    <Modal
      title="Contractor Search"
      closeClick={closeModal}
      maxWidth="980px"
      disableDrag
      needConfirm={false}
      className="contractor-search-modal"
      dataTestId="csm"
    >
      <div className="contractor-search__filters" data-test-id="csm-filters">
        <div
          className={`contractor-search__row contractor-search__row--top ${
            isAuxConEnabled ? 'contractor-search__row--tax-id-enabled' : ''
          }`}
        >
          <div className="modal-search-item">
            <ControlledInput
              control={control}
              name="contractorName"
              title="Contractor Name"
              placeholder="Search contractor"
              maxWidth="100%"
              dataTestId="csm-name"
              removePlaceholder
            />
          </div>

          {isAuxConEnabled && (
            <div className="modal-search-item">
              <ControlledInput
                control={control}
                name="tin"
                title="Tax Identification Number"
                placeholder="Tax ID number"
                mask="99-9999999"
                rules={{
                  pattern: {
                    value: /^\d*$/,
                    message:
                      'Tax Identification Number must contain only numbers',
                  },
                  maxLength: {
                    value: 9,
                    message: 'Tax Identification Number cannot exceed 9 digits',
                  },
                  validate: (value) =>
                    validations.length('Tax Identification Number', value, 9),
                }}
                maxWidth="100%"
                dataTestId="csm-tin"
              />
            </div>
          )}

          <div className="modal-search-item">
            <ControlledSelect
              control={control}
              name="los"
              title="Level of Service"
              selectOptions={contractorsLos}
              isMulti={true}
              maxWidth="100%"
              requestByAsync={loadLos}
              dataTestId="csm-service"
            />
          </div>
        </div>

        <div className="contractor-search__row contractor-search__row--bottom">
          <div className="modal-search-item">
            <ControlledSelect
              control={control}
              name="state"
              title="State"
              selectOptions={states}
              maxWidth="100%"
              dataTestId="csm-state"
              requestByAsync={loadStates}
              isClearable
            />
          </div>

          <div className="modal-search-item">
            <ControlledSelect
              control={control}
              name="dispatchPlatform"
              title="ATMS Software"
              selectOptions={contractorsAtms}
              maxWidth="100%"
              dataTestId="csm-atms"
              requestByAsync={loadAtms}
              isClearable
            />
          </div>

          <div className="modal-search-item">
            <ControlledSelect
              control={control}
              name="contractorType"
              title="Contractor Type"
              selectOptions={contractorsType}
              isMulti={true}
              maxWidth="100%"
              dataTestId="csm-type"
              requestByAsync={loadContractorTypes}
            />
          </div>
        </div>
      </div>

      <div className="contractor-search__results">
        {[
          {
            ...searchContractorData,
            title: 'Search Result',
            isLoading: isContractorLoading,
            argument: { body: filters },
          },
        ].map((config) => (
          <TableSection
            dataTestId={`csm-res-${config.title}`}
            key={config.title}
            title={config.title}
            isLoading={config.isLoading}
            openModal={config.openModal}
            permissions={config.permissions}
            requestTablePageChange={config.requestTablePageChange}
            tableCustomClassName={[
              'contractor-search-results-table',
              config.tableCustomClassName,
            ]
              .filter(Boolean)
              .join(' ')}
            hasCreatePermission={false}
            showTable={true}
            tableName={config.table}
            sortBy={config.sortBy}
            sortDir={config.sortDir}
            onEditClick={config.handleEditClick}
            rows={config.rows}
            page={config.page}
            columns={config.columns}
            totalElements={config.totalElements}
            size={config.size}
            totalPages={config.totalPages}
            modalName={config.modalName}
            addBtnTitle={config.addBtnTitle}
            argument={config.argument}
            isFilterable={false}
            arrowIconMember={true}
            handleSelectedRow={handleClickEdit}
            infiniteScroll={true}
            infiniteScrollThreshold={0.9}
          />
        ))}
      </div>
    </Modal>
  );
};

export default ContractorSearchModal;
